import { isNonNil } from '@shady-pines/types';
import { isNil } from 'lodash';
import { useState } from 'react';
import type { StorageBucket } from '../types';
import { useSupabase } from '../use-supabase';

const toBase64 = (arrayBuffer: ArrayBuffer): string => {
  const uint8Array = new Uint8Array(arrayBuffer);
  const binaryString = uint8Array.reduce((data, byte) => data + String.fromCharCode(byte), '');
  return btoa(binaryString);
};

type UploadResponse = {
  id: string;
  path: string;
  fullPath: string;
};

type ErrorResponse = {
  error: string;
};

export const useSupabaseStorage = () => {
  const supabase = useSupabase();
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = async ({
    bucket,
    folder,
    file,
    fileName,
    fileType,
  }: {
    bucket: StorageBucket;
    folder?: string;
    file: ArrayBuffer;
    fileType: string;
    fileName?: string;
  }): Promise<UploadResponse> => {
    setIsUploading(true);
    try {
      const name = isNonNil(fileName) ? fileName : '';
      const fileNameWithFolder = folder ? `${folder}/${name}` : name;

      if (isNil(fileNameWithFolder)) {
        throw new Error('File name is required');
      }

      const base64 = toBase64(file);

      const { data } = await supabase.functions.invoke<UploadResponse | ErrorResponse>(
        `file-upload/${bucket}/${fileNameWithFolder}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            file: base64,
            fileType,
          }),
        },
      );

      if (!data) {
        throw new Error('No response data received');
      }

      if ('error' in data) {
        throw new Error(data.error);
      }

      return data;
    } finally {
      setIsUploading(false);
    }
  };

  return { uploadFile, isUploading };
};
